import { useMemo, useState } from "react";
import type React from "react";
import { AlertCircle, Check, Search, X } from "lucide-react";
import BottomSheet from "./BottomSheet";
import { roleLabel } from "./MenuAccountRow";
import { Splash } from "./Splash";
import { ENV } from "@/config/env";
import { path } from "@/utils/common";
import { AUTH_ME } from "@/utils/const";
import {
  clearTenantScopedLocalState,
  listSwitchableTenants,
  needsReloginForMfa,
} from "@/utils/tenantSwitch";
import { authFetch } from "@shared-ui/utils/api";
import type {
  WhoamiMe,
  WhoamiMembership,
} from "@shared-domain/auth-service/contract";

/** 絞り込み入力を出す候補件数のしきい値 */
const FILTER_THRESHOLD = 8;

interface TenantSwitchSheetProps {
  isOpen: boolean;
  onClose: () => void;
  auth: WhoamiMe;
}

/**
 * PRODUCT_DEV-631: ログアウトせずにテナントを切り替えるシート。
 *
 * テナントは JWT ではなく `Users.currentTenantId` で解決されるため、
 * `PATCH /auth/me`（callPatchCurrentTenant）でカレントテナントを書き換えて
 * トップへフルリロードすれば切替が完了する。所属していないテナント・Unizo 未契約
 * テナントは同 API の authMiddleware が 403 で弾くため、UI 側の候補生成が漏れても
 * 越境は起きない。
 */
export const TenantSwitchSheet: React.FC<TenantSwitchSheetProps> = ({
  isOpen,
  onClose,
  auth,
}) => {
  const [keyword, setKeyword] = useState("");
  const [switchingTenantId, setSwitchingTenantId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 絞り込み文字列・エラーは「閉じたら unmount」で捨てる想定（親が開いている間だけ
  // マウントする）。ここで state をリセットする effect は持たない。
  const tenants = useMemo(() => listSwitchableTenants(auth), [auth]);
  const showFilter = tenants.length >= FILTER_THRESHOLD;
  const visibleTenants = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((t) => t.tenantName.toLowerCase().includes(q));
  }, [tenants, keyword]);

  const handleSelect = async (tenant: WhoamiMembership) => {
    if (switchingTenantId) return;
    if (tenant.tenantId === auth.currentTenantId) return;

    // MFA 必須テナントへ入る際の TOTP 設定強制は Portal のログインフローが担うため、
    // 現在テナントが MFA 不要な状態からの切替だけは Portal 再ログインへ回す。
    if (needsReloginForMfa(auth, tenant)) {
      // new URL(path, base) で AUTH_PORTAL_URL 末尾スラッシュの有無を吸収する
      // （Settings 画面のポータル遷移と同じイディオム）
      const portalUrl = new URL("", ENV.AUTH_PORTAL_URL);
      portalUrl.searchParams.set("productId", ENV.PRODUCT_ID);
      portalUrl.searchParams.set("tenantId", tenant.tenantId);
      window.location.assign(portalUrl.toString());
      return;
    }

    setError(null);
    setSwitchingTenantId(tenant.tenantId);
    try {
      // shared-ui の apiPatch/callPatchCurrentTenant は使わない。handleApiError が 403 で
      // `/403` へ遷移したまま永久 pending になり、catch に到達しない＝このシート内で
      // 失敗を説明できないため（切替先テナントの IP 制限で現実的に起きる）。
      // 401 のリフレッシュ再試行は authFetch がそのまま担う。
      const response = await authFetch(AUTH_ME, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentTenantId: tenant.tenantId,
          tenantId: tenant.tenantId,
        }),
      });

      if (!response.ok) {
        setSwitchingTenantId(null);
        setError(
          response.status === 403
            ? "このテナントには切り替えられませんでした。アクセス許可（IP 制限など）を確認してください。"
            : "テナントを切り替えられませんでした。時間をおいて再度お試しください。",
        );
        return;
      }

      // 前テナントの検索履歴・presetId 等は localStorage に残るため明示的に破棄してから
      // ルートへフルリロードする（リロードだけでは消えない）
      clearTenantScopedLocalState();
      window.location.assign(path(""));
    } catch {
      // 通信断など。認可失敗の詳細はコンソールにも残さない
      console.error("Failed to switch tenant");
      setSwitchingTenantId(null);
      setError("テナントを切り替えられませんでした。通信状況を確認してください。");
    }
  };

  // 切替 API 〜 リロードの間はメニュー/シートより前面を覆い、二重操作を防ぐ
  if (switchingTenantId) {
    return (
      <div className="fixed inset-0 z-[90] bg-white">
        <Splash message="テナントを切り替えています..." />
      </div>
    );
  }

  // DESIGN.md「ボトムシートの z-index は 80 に統一」に合わせる。BottomSheet の既定は
  // 歴史的に z-50 のままで、メニュー（同じく z-50）の上に重ねるには明示指定が要る。
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} zIndexClass="z-[80]">
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <h2 className="text-lg font-bold text-neutral-900">テナントを切り替え</h2>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-neutral-100 transition-colors"
          aria-label="閉じる"
        >
          <X size={18} className="text-neutral-700" />
        </button>
      </div>

      {error && (
        <div className="mx-5 mb-2 flex items-start gap-2 rounded-lg bg-danger/10 px-3 py-2">
          <AlertCircle size={14} className="text-danger flex-none mt-0.5" />
          <span className="text-[12px] text-danger">{error}</span>
        </div>
      )}

      {showFilter && (
        <div className="px-5 pb-2">
          <div className="flex items-center gap-2 rounded-lg bg-neutral-100 px-3 py-2">
            <Search size={14} className="text-neutral-500 flex-none" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="テナント名で絞り込み"
              className="flex-1 min-w-0 bg-transparent text-sm text-neutral-900 placeholder:text-neutral-400 outline-none"
            />
          </div>
        </div>
      )}

      <div className="px-5 pb-4">
        {visibleTenants.length === 0 ? (
          <p className="py-6 text-center text-sm text-neutral-500">
            {tenants.length === 0
              ? "切り替えできるテナントがありません。"
              : "一致するテナントがありません。"}
          </p>
        ) : (
          visibleTenants.map((tenant) => {
            const isCurrent = tenant.tenantId === auth.currentTenantId;
            return (
              <button
                key={tenant.tenantId}
                type="button"
                onClick={() => handleSelect(tenant)}
                disabled={isCurrent}
                aria-current={isCurrent ? "true" : undefined}
                className={`flex items-center gap-3 w-full px-1 py-3 text-left bg-transparent border-0 rounded-md transition-colors ${
                  isCurrent ? "cursor-default" : "cursor-pointer hover:bg-neutral-100"
                }`}
              >
                <div className="w-5 flex-none flex items-center justify-center">
                  {isCurrent && <Check size={16} className="text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`text-[15px] truncate ${
                      isCurrent
                        ? "font-bold text-primary"
                        : "font-medium text-neutral-900"
                    }`}
                  >
                    {tenant.tenantName}
                  </div>
                  {isCurrent && (
                    <div className="text-[10px] text-neutral-500">現在のテナント</div>
                  )}
                </div>
                <span className="text-[11px] text-neutral-500 flex-none">
                  {roleLabel(tenant.role)}
                </span>
              </button>
            );
          })
        )}
      </div>
    </BottomSheet>
  );
};

export default TenantSwitchSheet;
